import { useCreateOrganizationMutation } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { CreateOrganizationRequest, Services } from '@consigli/types';
import { FC, useEffect, useState } from 'react';

import { SheetForm } from '@/components';
import { errorToast, successToast } from '@/components/toast';

import { BasicInformationFields } from './basic-information-fieldset';
import { CreateOrganizationFormData, FormErrors } from './types';
import { validateOrganizationForm } from './validator';

interface AddOrganizationFormProps {
  onSubmit?: (data: CreateOrganizationRequest) => Promise<void>;
  isSubmitting?: boolean;
  onCancel?: () => void;
  trigger?: React.ReactNode;
  onFormChange?: (formData: CreateOrganizationFormData) => void;
  onValidationChange?: (
    isValid: boolean,
    errors: Partial<Record<keyof CreateOrganizationFormData, string>>,
  ) => void;
  validationErrors?: Partial<Record<keyof CreateOrganizationFormData, string>>;
}

/**
 * Services a new organization starts with ticked: every service, less the ones
 * still restricted to superusers or not yet released everywhere. Ticking one of
 * those here would enable it for the organization's admins, so it has to be
 * chosen, not inherited. INS-750 removes ERW from this list once it is released.
 */
const RESTRICTED_SERVICES: readonly Services[] = [
  Services.ENVIRONMENT_REPORT_AGENT,
  Services.LIGHTNING_DESIGNER,
];

export const DEFAULT_AVAILABLE_SERVICES: Services[] = Object.values(Services).filter(
  (service) => !RESTRICTED_SERVICES.includes(service),
);

const defaultValues: CreateOrganizationFormData = {
  name: '',
  type: null,
  availableServices: DEFAULT_AVAILABLE_SERVICES,
};

/**
 * Form for adding a new organization
 * @param onSubmit - The function to handle the form submission
 * @param trigger - The trigger for the form
 * @param onFormChange - The function to handle form changes
 * @param onValidationChange - The function to handle validation changes
 * @param validationErrors - The validation errors
 * @returns The Add Organization form
 */
export const AddOrganizationForm: FC<AddOrganizationFormProps> = ({
  onSubmit,
  trigger,
  onFormChange,
  onValidationChange,
  validationErrors = {},
}) => {
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateOrganizationFormData>(defaultValues);
  const [isOpen, setIsOpen] = useState(false);

  const [createOrganization, { isLoading }] = useCreateOrganizationMutation();

  const handleFormSubmit = async (): Promise<boolean> => {
    // Validate form and set errors if validation fails
    const validation = validateOrganizationForm(formData);

    if (!validation.isValid) {
      // Set validation errors to display them to the user
      setErrors(validation.errors);
      return false;
    }

    setIsSubmitting(true);
    try {
      const requestData: CreateOrganizationRequest = {
        name: formData.name,
        type: formData.type!,
        availableServices: formData.availableServices,
      };

      if (onSubmit) {
        await onSubmit(requestData);
      } else {
        await createOrganization({ createOrg: requestData }).unwrap();
        successToast({ message: 'Organization created successfully' });
      }

      // Reset form data when sheet is closed
      setFormData(defaultValues);
      setIsOpen(false);
      return true;
    } catch (caughtError: unknown) {
      // Extract field-specific errors from API response
      const fieldErrors: FormErrors = {};

      // Check if we have an error object with data property
      if (caughtError && typeof caughtError === 'object' && 'data' in caughtError) {
        const errorData = (caughtError as { data: Record<string, string | string[]> }).data;

        // Handle field-specific errors like {"name":["organization with this name already exists."]}
        Object.keys(errorData).forEach((field) => {
          const fieldErrorValues = Array.isArray(errorData[field])
            ? (errorData[field] as string[])
            : [errorData[field] as string];

          // Map backend field names to frontend field names
          let frontendField: keyof CreateOrganizationFormData;
          switch (field) {
            case 'name':
              frontendField = 'name';
              break;
            case 'type':
              frontendField = 'type';
              break;
            case 'available_services':
              frontendField = 'availableServices';
              break;
            default:
              frontendField = 'name'; // fallback to name field
          }

          // Take the first error message for the field
          fieldErrors[frontendField] = fieldErrorValues[0];
        });
      }

      // Set field-specific errors to display them inline with the form fields
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        return false;
      }

      // Fallback to generic error toast if no field-specific errors found
      errorToast({ message: 'Failed to create organization. Please try again.' });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(defaultValues);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // Reset form data when sheet is closed
      setFormData(defaultValues);
    }
  };

  const updateFormData = (field: keyof CreateOrganizationFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFieldChange = (field: keyof CreateOrganizationFormData, value: unknown) => {
    const newFormData = { ...formData, [field]: value };
    updateFormData(field, value);

    // Validate all fields and notify parent
    const validation = validateOrganizationForm(newFormData);
    if (onValidationChange) {
      onValidationChange(validation.isValid, validation.errors);
    }
  };

  // Update parent component when form data changes
  useEffect(() => {
    if (onFormChange) {
      onFormChange(formData);
    }
  }, [formData, onFormChange]);

  // Merge external validation errors with local errors
  useEffect(() => {
    if (Object.keys(validationErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...validationErrors }));
    }
  }, [validationErrors]);

  // Validate form on initial load and notify parent
  useEffect(() => {
    const validation = validateOrganizationForm(formData);
    if (onValidationChange) {
      onValidationChange(validation.isValid, validation.errors);
    }
  }, [formData, onValidationChange]);

  return (
    <SheetForm
      title="Add New Organization"
      description="Add a new organization to the system"
      trigger={trigger || <Button>Add Organization</Button>}
      onSubmit={handleFormSubmit}
      onReset={handleReset}
      submitLabel="Create Organization"
      resetLabel="Reset"
      isSubmitting={isSubmitting || isLoading}
      showReset={true}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <div className="space-y-6">
        <BasicInformationFields
          formData={formData}
          errors={errors}
          onFieldChange={handleFieldChange}
        />
      </div>
    </SheetForm>
  );
};
